import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Box,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  InputAdornment,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import ReactInputMask from "react-input-mask";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import MedicalHistoryTable from "./MedicalHistoryTable/MedicalHistoryTable";
import DonorConsentDialog from "./DonorConsentDialog";

const BloodDonor = () => {
  const [formData, setFormData] = useState({
    donorId: "",
    name: "",
    fatherName: "",
    bloodGroup: "",
    gender: "Male",
    age: "",
    occupation: "",
    city: "",
    // area: "",
    cnic: "",
    cellNo: "",
    donationType: "DONATION",
    lastDonationDate: "",
    centerOfDonation: "",
    donorSignature: "",
    doctorSignature: "",
    generalPhysicalAppearance: "",
    status: "Active",
    medicalHistory: [
      {
        question: "",
        answer: false,
        remarks: "",
      },
    ],
    vitals: {
      pulse: "",
      bloodPressure: "",
      temperature: "",
      height: "",
      weight: "",
    },
    hemoglobin: "",
    anyOtherInformation: "",
    remarks: "Fit",
  });

  // Store the full medical history data including checked state
  const [fullMedicalHistory, setFullMedicalHistory] = useState<
    { question: string; answer: boolean; remarks: string }[]
  >([]);
  const [error, setError] = useState<{ [key: string]: string }>({
    name: "",
    fatherName: "",
    age: "",
    occupation: "",
    city: "",
    cnic: "",
    cellNo: "",
    date: "",
    lastDonationDate: "",
    bloodGroup: "",
  });

  const [consentOpen, setConsentOpen] = useState(false);
  const loc = useLocation();
  const { idd, data } = loc.state || {};
  const [id, setID] = useState<string>(idd || "");
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      const lastDonationDate = data.lastDonationDate || data.donationDate || "";
      setFormData({
        ...data,
        lastDonationDate: lastDonationDate
          ? String(lastDonationDate).split("T")[0]
          : "",
        centerOfDonation: data.centerOfDonation || "",
        donorSignature: data.donorSignature || "",
        doctorSignature: data.doctorSignature || "",
        remarks: data.remarks === "Unfit" ? "Unfit" : "Fit",
      });
    } else {
      return;
    }
  }, [data]);

  const handleChangeVitalsBlood = (e: any) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      vitals: {
        ...prevState?.vitals,
        [name]: value,
      },
    }));
  };

  const handleChangeVitals = (e: any) => {
    const { name, value } = e.target;
    const parsedValue = Number(value);
    if (!isNaN(parsedValue)) {
      if (name === "pulse" && parsedValue <= 280) {
        setFormData(prevState => ({
          ...prevState,
          vitals: {
            ...prevState.vitals,
            [name]: parsedValue,
          },
        }));
      } else if (
        name === "temperature" &&
        parsedValue >= 92 &&
        parsedValue <= 107
      ) {
        setFormData(prevState => ({
          ...prevState,
          vitals: {
            ...prevState?.vitals,
            [name]: parsedValue,
          },
        }));
      } else if (
        (name === "height" || name === "weight") &&
        parsedValue <= 99
      ) {
        setFormData(prevState => ({
          ...prevState,
          vitals: {
            ...prevState?.vitals,
            [name]: parsedValue,
          },
        }));
      }
    }
  };

  const handleChangeDate = (newValue: any) => {
    if (newValue) {
      const formattedDateForDB = newValue?.format("YYYY-MM-DD");
      setFormData(prevData => ({
        ...prevData,
        lastDonationDate: formattedDateForDB,
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        lastDonationDate: "",
      }));
    }
  };

  const handleChange = (e: any, newValue?: any) => {
    const { name, value, type, checked, dataset } = e.target;
    if (type === "checkbox" && dataset.index !== undefined) {
      const updatedHistory = [...formData?.medicalHistory];
      updatedHistory[dataset.index].answer = checked;
      setFormData(prevData => ({
        ...prevData,
        medicalHistory: updatedHistory,
      }));
    } else if (name === "age") {
      const numericValue = value === "" ? "" : Number(value);
      setFormData(prevData => ({
        ...prevData,
        [name]: numericValue,
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
      }));
    }
  };
  const handleMedicalHistoryChange = (updatedMedicalHistory: any[]) => {
    // Store the full medical history data including checked state
    setFullMedicalHistory(updatedMedicalHistory);

    // Update formData with all questions, but we'll filter before API submission
    setFormData(prevData => ({
      ...prevData,
      medicalHistory: updatedMedicalHistory.map(
        ({ question, answer, remarks }) => ({
          question,
          answer,
          remarks,
        })
      ),
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      fatherName: "",
      age: "",
      occupation: "",
      city: "",
      cnic: "",
      cellNo: "",
      lastDonationDate: "",
      bloodGroup: "",
    };

    let isValid = true;

    // Validate required fields
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }
    if (!formData.fatherName.trim()) {
      newErrors.fatherName = "Father Name is required";
      isValid = false;
    }
    const ageNum = Number(formData.age);
    if (!formData.age) {
      newErrors.age = "Valid age (1-99) is required";
      isValid = false;
    }
    if (!formData.city.trim()) {
      newErrors.city = "City/Address is required";
      isValid = false;
    }
    if (!formData.cnic || !/^\d{13}$/.test(formData.cnic.replace(/-/g, ""))) {
      newErrors.cnic = "Valid CNIC (13 digits) is required";
      isValid = false;
    }
    if (
      !formData.cellNo ||
      !/^\d{11}$/.test(formData.cellNo.replace(/[^0-9]/g, ""))
    ) {
      newErrors.cellNo = "Valid cell number (11 digits) is required";
      isValid = false;
    }
    if (!formData.bloodGroup) {
      newErrors.bloodGroup = "Blood Group is required";
      isValid = false;
    }

    // Update error state
    setError(newErrors);
    return isValid;
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const vitalsAsStrings = {
      pulse: formData.vitals.pulse?.toString() || "",
      bloodPressure: formData.vitals.bloodPressure?.toString() || "",
      temperature: formData.vitals.temperature?.toString() || "",
      height: formData.vitals.height?.toString() || "",
      weight: formData.vitals.weight?.toString() || "",
    };

    // Create a copy of formData with only checked medical history questions
    const dataToSubmit = {
      ...formData,
      lastDonationDate: formData.lastDonationDate || null,
      vitals: vitalsAsStrings,
      medicalHistory: fullMedicalHistory
        .filter((question: any) => question.checked)
        .map(({ question, answer, remarks }: any) => ({
          question,
          answer,
          remarks,
        })),
    };

    if (id) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_BLOOD_BANK}/donor/updateDonorById/${id}`,
          dataToSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.info(response?.data?.message);
        } else {
          setID(response?.data?.data?._id);
          navigate("/donor-history");

          toast.success(response?.data?.message);
        }
      } catch (error) {
        console.error("Error saving data:", error);
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BLOOD_BANK}/donor/createDonor`,
          dataToSubmit,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.info(response?.data?.message);
        } else {
          setID(response?.data?.data?._id);
          navigate("/donor-screening", {
            state: {
              id: response?.data?.data?._id,
              data: dataToSubmit,
            },
          });
          toast.success(response?.data?.message);
        }
      } catch (error) {
        console.error("Error saving data:", error);
      }
    }
  };

  const handleClear = () => {
    setFormData({
      donorId: "",
      name: "",
      fatherName: "",
      bloodGroup: "",
      gender: "Male",
      age: "",
      occupation: "",
      city: "",
      // area: "",
      cnic: "",
      cellNo: "",
      donationType: "DONATION",
      lastDonationDate: "",
      centerOfDonation: "",
      donorSignature: "",
      doctorSignature: "",
      generalPhysicalAppearance: "",
      status: "ACTIVE",
      medicalHistory: [
        {
          question: "",
          answer: false,
          remarks: "",
        },
      ],
      vitals: {
        pulse: "",
        bloodPressure: "",
        temperature: "",
        height: "",
        weight: "",
      },
      hemoglobin: "",
      anyOtherInformation: "",
      remarks: "Fit",
    });
    setFullMedicalHistory([]);
    setID("");
  };

  return (
    <Box>
      <Box sx={{ background: "#fff", p: 0.5, borderRadius: "8px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
          {id ? "Update Blood Donor" : "Blood Donor"}
        </Typography>
      </Box>
      <Box
        sx={{ background: "#fff", px: 2, py: 1, mt: 1, borderRadius: "8px" }}
      >
        <form onSubmit={handleSubmit}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold", mb: 1 }}>
            Donor details
          </Typography>
          <Grid container spacing={1}>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label={
                  <>
                    Name <span className="text-red-500">*</span>
                  </>
                }
                name="name"
                value={formData?.name}
                onChange={handleChange}
                fullWidth
                // required
                size="small"
              />
              <p className="text-red-500 text-[10px] ml-2">{error?.name}</p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label={
                  <>
                    Father Name <span className="text-red-500">*</span>
                  </>
                }
                name="fatherName"
                value={formData?.fatherName}
                onChange={handleChange}
                fullWidth
                size="small"

                // required
              />
              <p className="text-red-500 text-[10px] ml-2">
                {error?.fatherName}
              </p>
            </Grid>

            <Grid item md={2.4} sm={3} xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>
                  Gender <span className="text-red-500">*</span>
                </InputLabel>
                <Select
                  className="custom-input"
                  name="gender"
                  value={formData?.gender}
                  onChange={handleChange}
                  label={
                    <>
                      Gender <span className="text-red-500">*</span>
                    </>
                  }
                  size="small"
                  // sx={{
                  //   borderRadius: "12px",
                  //   background: "#F5F5FB",
                  //   fontSize: "12px",
                  //   boxShadow: "none",
                  //   height: "38px",
                  //   "& fieldset": { border: "none" },
                  // }}
                  fullWidth
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label={
                  <>
                    Age <span className="text-red-500">*</span>
                  </>
                }
                name="age"
                type="number"
                // required
                value={formData?.age}
                onChange={e => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^\d{1,2}$/.test(value) && Number(value) <= 99)
                  ) {
                    handleChange(e);
                  }
                }}
                fullWidth
                size="small"
                inputProps={{ maxLength: 2, min: 0, max: 99 }}
              />
              <p className="text-red-500 text-[10px] ml-2">{error?.age}</p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Occupation"
                name="occupation"
                // required
                value={formData?.occupation}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <p className="text-red-500 text-[10px] ml-2">
                {error?.occupation}
              </p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel className="custom-input">
                  Blood Group <span className="text-red-500 ">*</span>
                </InputLabel>
                <Select
                  className="custom-input"
                  name="bloodGroup"
                  value={formData?.bloodGroup}
                  onChange={handleChange}
                  label="Blood Group"
                  // required
                  // size="small"
                  // sx={{
                  //   borderRadius: "12px",
                  //   background: "#F5F5FB",
                  //   fontSize: "12px",
                  //   boxShadow: "none",
                  //   height: "38px",
                  //   "& fieldset": { border: "none" },
                  // }}
                  fullWidth
                >
                  <MenuItem value="A+">A+</MenuItem>
                  <MenuItem value="A-">A-</MenuItem>
                  <MenuItem value="B+">B+</MenuItem>
                  <MenuItem value="B-">B-</MenuItem>
                  <MenuItem value="O+">O+</MenuItem>
                  <MenuItem value="O-">O-</MenuItem>
                  <MenuItem value="AB+">AB+</MenuItem>
                  <MenuItem value="AB-">AB-</MenuItem>
                </Select>
              </FormControl>
              <p className="text-red-500 text-[10px] ml-2">
                {error?.bloodGroup}
              </p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <ReactInputMask
                mask="03999999999"
                value={formData?.cellNo}
                onChange={handleChange}
              >
                {inputProps => (
                  <TextField
                    className="custom-input"
                    {...inputProps}
                    label={
                      <>
                        Cell No <span className="text-red-500">*</span>
                      </>
                    }
                    name="cellNo"
                    fullWidth
                    // required
                    size="small"
                  />
                )}
              </ReactInputMask>
              <p className="text-red-500 text-[10px] ml-2">{error?.cellNo}</p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label={
                  <>
                    City/Address <span className="text-red-500">*</span>
                  </>
                }
                name="city"
                value={formData?.city}
                onChange={handleChange}
                fullWidth
                // required
                size="small"
              />
              <p className="text-red-500 text-[10px] ml-2">{error?.city}</p>
            </Grid>
            {/* <Grid item md={2.4} sm={3} xs={6}>
              <TextField
              className="custom-input"
                label="Area"
                name="area"
                value={formData?.area}
                onChange={handleChange}
                fullWidth
                size="small"
                
              />
            </Grid> */}
            <Grid item md={2.4} sm={3} xs={6}>
              <ReactInputMask
                mask="9999999999999"
                value={formData?.cnic}
                onChange={handleChange}
                // required
              >
                {inputProps => (
                  <TextField
                    className="custom-input"
                    {...inputProps}
                    label={
                      <>
                        CNIC <span className="text-red-500">*</span>
                      </>
                    }
                    name="cnic"
                    fullWidth
                    size="small"
                  />
                )}
              </ReactInputMask>
              <p className="text-red-500 text-[10px] ml-2">{error?.cnic}</p>
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="custom-input"
                  label="Last Donation Date"
                  name="lastDonationDate"
                  value={
                    formData?.lastDonationDate
                      ? dayjs(formData.lastDonationDate, "YYYY-MM-DD")
                      : null
                  }
                  onChange={handleChangeDate}
                  format="DD.MM.YYYY"
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      className: "custom-input",
                    },
                  }}
                  // sx={{
                  //   "& fieldset": {
                  //     border: "none",
                  //   },
                  //   "& .MuiInputBase-root": {
                  //     height: "38px",
                  //     fontSize: "12px",
                  //     overflow: "hidden",
                  //   },
                  //   "& .MuiOutlinedInput-root": {
                  //     borderRadius: "8px",
                  //     border: "none",
                  //     backgroundColor: "#F5F5FB",
                  //   },
                  //   "& .MuiCalendarPicker-root": {
                  //     overflow: "hidden",
                  //   },
                  //   "& .css-4jnixx-MuiStack-root": {
                  //     paddingTop: "0px",
                  //   },
                  //   width: "100%",
                  //   height: "38px",
                  // }}
                />
              </LocalizationProvider>
              <p className="text-red-500 text-[10px] ml-2">
                {error?.lastDonationDate}
              </p>
            </Grid>
          </Grid>
          <Grid container spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
            <Grid item md={4.8} sm={6} xs={12}>
              <TextField
                className="custom-input"
                label="General Physical Appearance"
                name="generalPhysicalAppearance"
                value={formData?.generalPhysicalAppearance}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item md={2.4} sm={6} xs={12}>
              <TextField
                className="custom-input"
                label="Center of Donation"
                name="centerOfDonation"
                value={formData?.centerOfDonation}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item md={2.4} sm={6} xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
                <Select
                  className="custom-input"
                  name="status"
                  value={formData?.status}
                  onChange={handleChange}
                  label="Status"
                  size="small"
                  // sx={{
                  //   borderRadius: "12px",
                  //   background: "#F5F5FB",
                  //   fontSize: "12px",
                  //   boxShadow: "none",
                  //   height: "38px",
                  //   "& fieldset": { border: "none" },
                  // }}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Deferred">Deferred</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={2.4} sm={6} xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: "12px" }}>
                  Donation Type
                </FormLabel>
                <RadioGroup
                  row
                  name="donationType"
                  value={formData?.donationType}
                  onChange={handleChange}
                  sx={{
                    height: "40px",
                    display: "row",
                    alignItems: "center",
                    gap: 0,
                  }}
                >
                  <FormControlLabel
                    value="DONATION"
                    control={
                      <Radio size="small" sx={{ p: 0.8, fontSize: "12px" }} />
                    }
                    label={<span style={{ fontSize: "10px" }}>Donation</span>}
                  />
                  <FormControlLabel
                    value="EXCHANGE"
                    control={
                      <Radio size="small" sx={{ p: 0.8, fontSize: "12px" }} />
                    }
                    label={<span style={{ fontSize: "10px" }}>Exchange</span>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <Typography
            sx={{ fontSize: "12px", fontWeight: "bold", mt: 2, mb: 1 }}
          >
            Donor Vitals
          </Typography>
          <Grid container spacing={1}>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Pulse"
                name="pulse"
                placeholder="0-280"
                type="number"
                value={formData?.vitals?.pulse}
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (!isNaN(value) && value <= 280) {
                    handleChangeVitals(e);
                  }
                }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">bpm</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Blood Pressure"
                name="bloodPressure"
                placeholder="120/90"
                value={formData?.vitals?.bloodPressure}
                // required
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleChangeVitalsBlood(e);
                    return;
                  }
                  const regex = /^(\d{1,3})\/?(\d{0,3})$/;
                  if (regex.test(value)) {
                    const parts = value.split("/");
                    const firstPart = Number.parseInt(parts[0], 10);
                    const secondPart = parts[1]
                      ? Number.parseInt(parts[1], 10)
                      : 0;
                    if (
                      firstPart >= 1 &&
                      firstPart <= 300 &&
                      secondPart <= firstPart
                    ) {
                      handleChangeVitalsBlood(e);
                    }
                  }
                }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">mmHg</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Temperature"
                name="temperature"
                placeholder="92-107"
                type="number"
                value={formData?.vitals?.temperature || ""}
                onChange={(e: any) => {
                  const { name, value } = e.target;
                  setFormData(prevState => ({
                    ...prevState,
                    vitals: {
                      ...prevState?.vitals,
                      [name]: value,
                    },
                  }));
                }}
                onBlur={(e: any) => {
                  const { name, value } = e.target;
                  const parsedValue = Number(value);
                  if (!isNaN(parsedValue)) {
                    if (parsedValue < 92 || parsedValue > 107) {
                      setFormData(prevState => ({
                        ...prevState,
                        vitals: {
                          ...prevState?.vitals,
                          [name]: "",
                        },
                      }));
                    } else {
                      handleChangeVitals(e);
                    }
                  } else {
                    setFormData(prevState => ({
                      ...prevState,
                      vitals: {
                        ...prevState?.vitals,
                        [name]: "",
                      },
                    }));
                  }
                }}
                fullWidth
                size="small"
                helperText={
                  formData?.vitals?.temperature &&
                  (Number(formData?.vitals?.temperature) < 92 ||
                    Number(formData?.vitals?.temperature) > 107) ? (
                    <span style={{ color: "red" }}>Temperature 92-107</span>
                  ) : (
                    ""
                  )
                }
                // error={
                //   Number(formData?.vitals?.temperature) < 92 ||
                //   Number(formData?.vitals?.temperature) > 107
                // }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">F</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Height"
                name="height"
                type="number"
                value={formData?.vitals?.height}
                onChange={(e: any) => {
                  const value = Number(e.target.value);
                  if (!isNaN(value) && value <= 99) {
                    handleChangeVitals(e);
                  }
                }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">inch</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Weight"
                name="weight"
                type="number"
                value={formData?.vitals?.weight}
                // required
                onChange={(e: any) => {
                  const value = Number(e.target.value);
                  if (!isNaN(value) && value <= 99) {
                    handleChangeVitals(e);
                  }
                }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">KG</InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item md={2.4} sm={3} xs={6}>
              <TextField
                className="custom-input"
                label="Hemoglobin"
                name="hemoglobin"
                type="number"
                value={formData?.hemoglobin}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item md={9.6} sm={6} xs={12}>
              <TextField
                className="custom-input"
                label="Any Other Information"
                name="anyOtherInformation"
                value={formData?.anyOtherInformation}
                onChange={handleChange}
                fullWidth
                multiline
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                  Medical History
                </Typography>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  onClick={() => setConsentOpen(true)}
                >
                  Donor Consent
                </Button>
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  mb: 1,
                }}
              >
                <MedicalHistoryTable
                  formData={formData}
                  handleMedicalHistoryChange={handleMedicalHistoryChange}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{ fontSize: "12px", fontWeight: "bold", color: "#111" }}
                >
                  Remarks
                </FormLabel>
                <RadioGroup
                  row
                  name="remarks"
                  value={formData?.remarks || "Fit"}
                  onChange={handleChange}
                  sx={{ alignItems: "center" }}
                >
                  <FormControlLabel
                    value="Fit"
                    control={<Radio size="small" sx={{ p: 0.8 }} />}
                    label={<span style={{ fontSize: "12px" }}>Fit</span>}
                  />
                  <FormControlLabel
                    value="Unfit"
                    control={<Radio size="small" sx={{ p: 0.8 }} />}
                    label={<span style={{ fontSize: "12px" }}>Unfit</span>}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
              >
                <Button onClick={handleClear} variant="outlined">
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  // disabled={
                  //   !formData?.cellNo ||
                  //   !formData?.gender ||
                  //   !formData?.fatherName ||
                  //   !formData?.bloodGroup ||
                  //   !formData?.donationType ||
                  //   !formData?.cnic ||
                  //   !formData?.donationDate
                  //   // !formData?.vitals?.pulse ||
                  //   // !formData?.vitals?.height ||
                  //   // !formData?.vitals?.weight ||
                  //   // (Number(formData?.vitals?.temperature) < 92 &&
                  //   // Number(formData?.vitals?.temperature) > 107)
                  // }
                >
                  {id ? "Update" : "Submit"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
        <DonorConsentDialog
          open={consentOpen}
          onClose={() => setConsentOpen(false)}
          donorSignature={formData.donorSignature}
          doctorSignature={formData.doctorSignature}
          onDonorSignatureChange={value =>
            setFormData(prev => ({ ...prev, donorSignature: value }))
          }
          onDoctorSignatureChange={value =>
            setFormData(prev => ({ ...prev, doctorSignature: value }))
          }
        />
      </Box>
    </Box>
  );
};

export default BloodDonor;
